import { Ellipsis } from '@zoom/components';
import classnames from 'classnames';
import React from 'react';
import { FieldRenderProps } from 'react-final-form';

type FilterButtonProps = {
  checked?: boolean;
  className?: string;
  dataTestId?: string;
  disabled?: boolean;
  name: string;
  onChange: (e: any) => void;
  type: 'radio' | 'checkbox';
  value?: string;
} & React.PropsWithChildren;

type FinalFormFilterButtonProps = FieldRenderProps<string> &
  Pick<FilterButtonProps, 'className' | 'dataTestId' | 'disabled'>;

export const FilterButton: React.FC<FilterButtonProps> = ({
  checked = false,
  children,
  className,
  dataTestId,
  disabled = false,
  name,
  onChange,
  type,
  value,
}) => (
  // See https://github.com/jsx-eslint/eslint-plugin-jsx-a11y/issues/633
  // Rule doesn't work correctly with nested input and `nesting` or `either` rule setting
  // eslint-disable-next-line jsx-a11y/label-has-associated-control
  <label className="filter-button" data-test-id={dataTestId}>
    <input
      checked={checked}
      className="filter-button__input sr-only"
      disabled={disabled}
      name={name}
      onChange={onChange}
      type={type}
      value={value}
    />
    {/* @ts-expect-error: Incompatible type of FC from React < v18 */}
    <Ellipsis className={classnames('filter-button__label', className)}>{children}</Ellipsis>
  </label>
);

/**
 * Example Usage:
 *
 * ```
 * <Field name="key" type="radio" value="value" component={FinalFormFilterButton} disabled>label</Field>
 * ```
 *
 * or
 *
 * ```
 * <Field name="key" type="checkbox">
 *   {(props) => <FinalFormFilterButton {...props}>label</FinalFormFilterButton>}
 * </Field>
 * ```
 *
 * or
 *
 * ```
 * <Field
 *   name="key"
 *   type="radio"
 *   value="value"
 *   render={(props) => (
 *     <FinalFormFilterButton {...props} disabled>
 *       label
 *     </FinalFormFilterButton>
 *   )}
 * />
 * ```
 */
export const FinalFormFilterButton: React.FC<FinalFormFilterButtonProps> = ({
  children,
  className,
  dataTestId,
  disabled,
  input: { checked, name, onChange, type, value },
}) => {
  if (type !== 'radio' && type !== 'checkbox') {
    throw new TypeError(`Incorrect input type "${type}" provided. Only "radio" and "checkbox" are possible.`);
  }

  return (
    <FilterButton
      checked={checked}
      className={className}
      dataTestId={dataTestId}
      disabled={disabled}
      name={name}
      onChange={onChange}
      type={type}
      value={value}
    >
      {children}
    </FilterButton>
  );
};
